import React from "react";

export const TEAL = "#007a9e";
export const PURPLE = "#6b3fa0";
export const bandOf = (v) => (v == null ? null : v >= 80 ? "Healthy" : v >= 60 ? "Caution" : "High Risk");
export const BAND_CELL = { Healthy: "bg-[#dcfce7] text-[#14532d]", Caution: "bg-[#fef3c7] text-[#713f12]", "High Risk": "bg-[#fee2e2] text-[#7f1d1d]" };
const fmt = (v) => (v == null ? "—" : Number.isInteger(v) ? String(v) : v.toFixed(1));
export const sgap = (g) => (g == null ? "—" : `${g > 0 ? "+" : ""}${g.toFixed(1)}`);
const pos = (v) => `${Math.max(0, Math.min(100, v))}%`;
const lineStyle = (g) => { const a = Math.abs(g); return a < 5 ? ["#9ca3af", 2] : a < 15 ? ["#ea580c", 3] : ["#b91c1c", 4]; };

// Each shape renders its own <svg> so no dynamic JSX child sits directly inside <svg>
// (dev-time instrumentation wraps dynamic children in an HTML <span>, which SVG won't render).
const SHAPES = {
  circle: (c, size, hollow) => <svg width={size} height={size} viewBox="0 0 14 14" aria-hidden className="shrink-0"><circle cx="7" cy="7" r="5.5" fill={hollow ? "#fff" : c} stroke={hollow ? c : "#fff"} strokeWidth={hollow ? 2.5 : 1.5} /></svg>,
  diamond: (c, size) => <svg width={size} height={size} viewBox="0 0 14 14" aria-hidden className="shrink-0"><path d="M7 0.8 L13.2 7 L7 13.2 L0.8 7 Z" fill={c} stroke="#fff" strokeWidth="1.5" /></svg>,
  square: (c, size) => <svg width={size} height={size} viewBox="0 0 14 14" aria-hidden className="shrink-0"><rect x="1.5" y="1.5" width="11" height="11" fill={c} stroke="#fff" strokeWidth="1.5" /></svg>,
  triangle: (c, size) => <svg width={size} height={size} viewBox="0 0 14 14" aria-hidden className="shrink-0"><path d="M7 1 L13 12.5 L1 12.5 Z" fill={c} stroke="#fff" strokeWidth="1.5" /></svg>,
};
export const Marker = ({ s, size = 14 }) => SHAPES[s.shape](s.color, size, s.hollow);

export const DumbbellLegend = ({ series }) => (
  <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[12.5px] text-[#374151]" data-testid="dumbbell-legend">
    {series.map((s) => <span key={s.key} className="inline-flex items-center gap-1.5"><Marker s={s} />{s.label}</span>)}
    <span className="inline-flex items-center gap-1.5"><span className="h-3 w-4 rounded-sm bg-[#fbe3e3] ring-1 ring-[#f3b4b4]" />High Risk &lt;60</span>
    <span className="inline-flex items-center gap-1.5"><span className="h-3 w-4 rounded-sm bg-[#fdf3d8] ring-1 ring-[#f0d68a]" />Caution 60–79</span>
    <span className="inline-flex items-center gap-1.5"><span className="h-3 w-4 rounded-sm bg-[#e7f6ec] ring-1 ring-[#a7dcb7]" />Healthy ≥80</span>
    <span className="inline-flex items-center gap-1.5"><span className="h-1 w-5 rounded bg-[#b91c1c]" />Gap: thicker and redder = larger</span>
  </div>
);

// One row per item on a shared 0–100 axis; lines connect the reference series to every other series.
export const DumbbellTrack = ({ values, series, refKey, provisional = false, labels = true }) => {
  const ref = values[refKey];
  const others = series.filter((s) => s.key !== refKey && values[s.key] != null);
  return (
    <div className="relative h-11" aria-hidden>
      <div className="absolute inset-x-0 top-1/2 flex h-3.5 -translate-y-1/2 overflow-hidden rounded-sm">
        <div className="h-full bg-[#fbe3e3]" style={{ width: "60%" }} />
        <div className="h-full bg-[#fdf3d8]" style={{ width: "20%" }} />
        <div className="h-full flex-1 bg-[#e7f6ec]" />
      </div>
      {[60, 80].map((t) => <div key={t} className="absolute top-1.5 bottom-1.5 border-l border-dashed border-[#6b7280]" style={{ left: `${t}%` }} />)}
      {ref != null && others.map((s) => {
        const [c, w] = lineStyle(values[s.key] - ref);
        const lo = Math.min(ref, values[s.key]), hi = Math.max(ref, values[s.key]);
        return <div key={s.key} className="absolute top-1/2 -translate-y-1/2" style={{ left: pos(lo), width: `${hi - lo}%`, height: 0, borderTop: `${w}px ${provisional ? "dashed" : "solid"} ${c}` }} />;
      })}
      {series.filter((s) => values[s.key] != null).map((s, i) => (
        <div key={s.key} className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2" style={{ left: pos(values[s.key]), zIndex: s.key === refKey ? 3 : 2 }}>
          <Marker s={s} />
          {labels && (
            <span className={`absolute left-1/2 -translate-x-1/2 whitespace-nowrap text-[11px] font-bold ${i % 2 === 0 ? "-top-4" : "top-3.5"}`} style={{ color: s.color === TEAL ? "#00607d" : s.color }}>{fmt(values[s.key])}</span>
          )}
        </div>
      ))}
    </div>
  );
};

export const DumbbellAxis = () => (
  <div className="relative h-4 text-[11px] text-[#4b5563]" aria-hidden>
    {[0, 60, 80, 100].map((v) => <span key={v} className="absolute -translate-x-1/2" style={{ left: `${v}%` }}>{v}</span>)}
  </div>
);

export const BandChangeChip = ({ from, to, testid }) => {
  const a = bandOf(from), b = bandOf(to);
  if (!a || !b || a === b) return null;
  const cls = { Healthy: "border-[#86efac] bg-[#dcfce7] text-[#14532d]", Caution: "border-[#fcd34d] bg-[#fef3c7] text-[#713f12]", "High Risk": "border-[#fca5a5] bg-[#fee2e2] text-[#7f1d1d]" }[b];
  return (
    <span className={`inline-block whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-semibold ${cls}`} data-testid={testid}>
      {a} → {b}
    </span>
  );
};
